import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "../../FrontOffice/OutPatients/OutpatientHome.css";

import {
  AppointmentCard,
  CommonPatientDetails,
  CommonGridToolBarWithFilterText,
  ReasonPopup,
} from "../../../Components/Common Components/CommonComponents";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from "../../../Components/Loader";
import { ImagePaths } from "../../../Utility/ImagePaths";
import { renderIconButton } from "../../Doctor/DoctorNotes/Common/DocCommonComponent";
import CollectAdvanceModal from "./CounsellorAdvanceCollect";
import CancelIcon from "@mui/icons-material/Cancel";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";
import { useCounsellorDate } from "./CounsellorDateContext";

const CounsellorAppointments = (props) => {
  const { filterDate } = useCounsellorDate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusCount, setStatusCount] = useState({});
  const [selectedStatus, setSelectedStatus] = useState("");
  const [menuRow, setMenuRow] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedToCancel, setSelectedToCancel] = useState(null);
  const [errorMsg, seterrorMsg] = useState(false);
  const [errorMsgText, seterrorMsgText] = useState("");
  const [successMsg, setsuccessMsg] = useState(false);
  const [successMsgText, setsuccessMsgText] = useState("");
  const pageSizeOptions = [10, 20, 30];
  const [openAdvance, setOpenAdvance] = useState(false);
  const [openSkip, setOpenSkip] = useState(false);
  const [daysConfirmOpen, setDaysConfirmOpen] = useState(false);
  const [daysConfirmRow, setDaysConfirmRow] = useState(null);
  const [ipConfirmOpen, setIpConfirmOpen] = useState(false);
  const [ipConfirmRow, setIpConfirmRow] = useState(null);

  const errorMessage = (msg) => {
    seterrorMsgText(msg);
    seterrorMsg(true);
    setIsLoading(false);
  };
  const successMessage = (msg) => {
    setsuccessMsgText(msg);
    setsuccessMsg(true);
    setIsLoading(false);
  };
  const msgClose = () => {
    seterrorMsg(false);
    seterrorMsgText("");
    setsuccessMsg(false);
    setsuccessMsgText("");
  };

  const normalizeStatus = (s) => {
    if (!s && s !== "") return "";
    switch (s) {
      case "Pending": return "Pending";
      case "Approved":
      case "approve":
      case "approved": return "Approved";
      case "Cancelled":
      case "canceled": return "Cancelled";
      case "arrived": return "arrived";
      case "scheduled": return "scheduled";
      case "pending":
      case "Waiting For Approval":
      case "pending_approval":
      case "pending approval": return "Waiting For Approval";
      default: return String(s).toLowerCase();
    }
  };

  const getDaysByType = (row, type) => {
    if (type === "IP") return row?.ip_days || "";
    if (type === "WALKIN") return row?.op_days || "";
    return "";
  };

  const onRecommendationChange = (row, value) => {
    setAppointments((prev) =>
      prev.map((r) => {
        if (r.appointment_id !== row.appointment_id) return r;
        const newDays = r.isDaysManuallyChanged ? r.days : getDaysByType(r, value);
        return {
          ...r,
          recommendation_type: value,
          days: newDays,
          isDaysManuallyChanged: ((value === "WALKIN" && r?.op_days == newDays) || (value === "IP" && r?.ip_days == newDays)) ? false : true,
        };
      })
    );
  };

  const onDaysChange = (row, value) => {
    if (value === "" || /^[0-9]+$/.test(value)) {
      setAppointments((prev) =>
        prev.map((r) =>
          r.appointment_id === row.appointment_id ? { ...r, days: value, isDaysManuallyChanged: true } : r
        )
      );
    }
  };

  const onStartDateChange = (row, newDate) => {
    setAppointments((prev) =>
      prev.map((r) => (r.appointment_id === row.appointment_id ? { ...r, start_date: newDate } : r))
    );
  };

  const fetchAppointments = useCallback(() => {
    setIsLoading(true);
    try {
      const dateKey = filterDate ? dayjs(filterDate).format("YYYY-MM-DD") : "";
      const qs = `?status=${selectedStatus || ""}&date=${dateKey}`;
      RestAPIService.getAll(Serviceurls.AYURVEDA_COUNSELLOR + qs)
        .then((res) => {
          if (res.data.status === "success") {
            const list = (res?.data?.data || []).map((row, idx) => {
              const recType = row?.recommendation_type === "WALKIN" ? "WALKIN" : "IP";
              const daysVal = recType === "WALKIN" ? row?.op_days : row?.ip_days;
              const days = daysVal !== undefined && daysVal !== null ? Number(daysVal) : "";
              return {
                ...row,
                id: row.appointment_id,
                sno: idx + 1,
                start_date: row?.start_date ? dayjs(row?.start_date, "DD-MM-YYYY") : null,
                days: days,
                status: row?.status,
                isDaysChanged: false,
              };
            });
            setAppointments(list);
            setStatusCount(res.data || {});
          } else {
            errorMessage(res.data.message || "Failed to fetch appointments");
          }
        })
        .catch((e) => errorMessage(e?.response?.data?.message || e.message))
        .finally(() => setIsLoading(false));
    } catch (e) {
      setIsLoading(false);
      errorMessage(e.message);
    }
  }, [filterDate, selectedStatus]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const updateAppointment = (payload) => {
    setIsLoading(true);
    return RestAPIService.updateWithOutId(payload, Serviceurls.AYURVEDA_COUNSELLOR)
      .then((res) => {
        if (res.data.status === "success") {
          successMessage(res.data.message || "Updated successfully");
          fetchAppointments();
          return res.data;
        } else {
          errorMessage(res.data.message || "Update failed");
          throw new Error(res.data.message || "Update failed");
        }
      })
      .catch((e) => {
        errorMessage(e?.response?.data?.message || e.message);
        throw e;
      })
      .finally(() => setIsLoading(false));
  };

  const onCancelClick = (row) => {
    setSelectedToCancel(row);
    setCancelDialogOpen(true);
  };

  const confirmCancel = () => {
    if (!selectedToCancel) {
      setCancelDialogOpen(false);
      return;
    }
    const payload = { status: "Cancelled", cancel_reason: "", appointment_id: selectedToCancel?.appointment_id };
    updateAppointment(payload);
    setCancelDialogOpen(false);
  };

  const onApproveClick = (row) => {
    const status = normalizeStatus(row.status);
    if (row?.days === "" || row?.start_date === null) {
      errorMessage("Days and start date are required");
      return;
    }
    if (status === "Waiting For Approval") return;
    if (row?.isDaysManuallyChanged) {
      setDaysConfirmRow(row);
      setDaysConfirmOpen(true);
      return;
    }
    if (row?.recommendation_type === "IP") {
      setIpConfirmRow(row);
      setIpConfirmOpen(true);
      return;
    }
    setOpenAdvance(true);
    setMenuRow(row);
  };

  const confirmDaysChangeAndRequestApproval = async () => {
    if (!daysConfirmRow) {
      setDaysConfirmOpen(false);
      return;
    }
    if (daysConfirmRow?.days === "" || daysConfirmRow?.start_date === null) {
      errorMessage("Days and start date are required");
      return;
    }
    const payload = {
      appointment_id: daysConfirmRow?.appointment_id,
      status: "Waiting For Approval",
      days: +daysConfirmRow?.days,
      start_date: dayjs(daysConfirmRow?.start_date).format("YYYY-MM-DD"),
      recommendation_type: daysConfirmRow?.recommendation_type,
    };
    try {
      await updateAppointment(payload);
    } finally {
      setDaysConfirmOpen(false);
      setDaysConfirmRow(null);
    }
  };

  const columns = [
    {
      field: "patient_names",
      headerName: "Patient's Details",
      flex: 0.2,
      renderCell: (params) => (
        <Box>
          <CommonPatientDetails data={params.row} />
        </Box>
      ),
      headerAlign: "center",
      align: "left",
    },
    {
      field: "doctor_name",
      headerName: "Consultant Doctor",
      flex: 0.15,
      renderCell: (params) => <Typography sx={{ fontSize: "0.9vw" }}>{params.row?.doctor_name || "-"}</Typography>,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.2,
      renderCell: (params) => {
        const s = normalizeStatus(params.row?.status || "pending");
        let bg = "#FFEFD8", color = "#DC8400";
        if (["Approved", "scheduled", "arrived"].includes(s)) {
          bg = "#ECFFE9";
          color = "#1E972A";
        } else if (s === "Cancelled") {
          bg = "#FFE9E9";
          color = "#B82121";
        } else if (s === "Waiting For Approval" || s === "Pending") {
          bg = "#FFF6E6";
          color = "#B87D00";
        }
        return (
          <Paper elevation={0} sx={{ backgroundColor: bg, border: `1px solid ${color}`, px: 1, py: 1.5, borderRadius: "3px", width: "80%", textAlign: "center" }}>
            <Typography sx={{ color, fontWeight: 600, fontSize: "0.85vw", textTransform: "capitalize" }}>{s}</Typography>
          </Paper>
        );
      },
      headerAlign: "center",
      align: "center",
    },
    {
      field: "start_date",
      headerName: "Start Date",
      flex: 0.2,
      renderCell: (params) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={params.row?.start_date ? dayjs(params.row.start_date, "DD-MM-YYYY") : null}
            onChange={(newVal) => onStartDateChange(params.row, newVal)}
            renderInput={(p) => <TextField {...p} size="small" />}
            minDate={dayjs().startOf("day")}
            inputFormat="DD-MM-YYYY"
          />
        </LocalizationProvider>
      ),
      headerAlign: "left",
      align: "left",
    },
    {
      field: "ip_days",
      headerName: "Days",
      flex: 0.1,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params?.row?.status === "Waiting For Approval" ? params?.row?.requested_days : params.row?.days || ""}
          onChange={(e) => onDaysChange(params.row, e.target.value)}
          inputProps={{ maxLength: 3, style: { width: "5vw" } }}
        />
      ),
    },
    {
      field: "recommendation_type",
      headerName: "Recommendation Type",
      flex: 0.15,
      renderCell: (params) => (
        <FormControl size="small" sx={{ width: "8vw" }}>
          <Select value={params.row?.recommendation_type || ""} onChange={(e) => onRecommendationChange(params.row, e.target.value)}>
            <MenuItem value="IP">IP</MenuItem>
            <MenuItem value="WALKIN">Walk-in</MenuItem>
          </Select>
        </FormControl>
      ),
    },
    {
      field: "approval_status",
      headerName: "Doctor Approval",
      flex: 0.15,
      renderCell: (params) => <Typography sx={{ fontSize: "0.9vw" }}>{params.row?.approval_status || "-"}</Typography>,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.15,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const status = normalizeStatus(params.row.status);
        const disableBtn = ["Cancelled", "Approved"].includes(status);
        const approveDisable = params.row?.status === "Waiting For Approval";
        const occupyBox = (IconComponent, color = "error") => (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ width: "3vw", height: "3vw" }}>
            <IconComponent color={color} sx={{ fontSize: "1.8vw" }} />
          </Box>
        );
        return disableBtn
          ? occupyBox(status === "Cancelled" ? CancelIcon : DoneOutlineIcon, status === "Cancelled" ? "error" : "primary")
          : (
            <Box display="flex" alignItems="center" gap={1} justifyContent="center">
              {renderIconButton("Cancel", ImagePaths.CancelRed.default, () => onCancelClick(params.row), disableBtn, "1.6vw")}
              {renderIconButton("Confirm", approveDisable ? ImagePaths.disableTick.default : ImagePaths.TickEnable.default, () => onApproveClick(params.row), approveDisable, "1.6vw")}
            </Box>
          );
      },
    },
  ];

  const advanceCollect = (datas = {}, comments = "", rowOverride = null) => {
    const row = rowOverride || menuRow;
    if (!row) return;
    try {
      const payment = [];
      if (datas?.cash) payment.push(1);
      if (datas?.card) payment.push(2);
      if (datas?.upi) payment.push(3);
      if (datas?.bank) payment.push(5);
      const data = {
        appointment_id: row.appointment_id,
        recommendation_type: row?.recommendation_type,
        start_date: dayjs(row?.start_date).format("YYYY-MM-DD"),
        days: row?.days,
        advance_amount: +datas?.advance || 0,
        cash_amount: +datas?.cashAmount || 0,
        card_amount: +datas?.cardAmount || 0,
        upi_amount: +datas?.upiAmount || 0,
        bank_amount: +datas?.bankAmount || 0,
        payment_mode: payment,
        upi_ref: datas?.UPINumber || "",
        card_ref: datas?.CardNumber || "",
        bank_ref: datas?.BankTfrNumber || "",
        ...(comments ? { skip_reason: comments } : {}),
        status: "Approved",
      };
      RestAPIService.updateWithOutId(data, Serviceurls.AYURVEDA_COUNSELLOR)
        .then((response) => {
          if (response?.data?.status === "success") {
            successMessage(response?.data?.message);
            setOpenSkip(false);
            setOpenAdvance(false);
            setMenuRow();
            setIpConfirmOpen(false);
            setIpConfirmRow(null);
            fetchAppointments();
          }
        })
        .catch((error) => errorMessage(error?.response?.data?.message || error?.message));
    } catch (error) {
      errorMessage(error.message);
    }
  };

  const confirmIpConversion = () => {
    if (!ipConfirmRow) {
      setIpConfirmOpen(false);
      return;
    }
    if (ipConfirmRow?.days === "" || ipConfirmRow?.start_date === null) {
      errorMessage("Days and start date are required");
      return;
    }
    advanceCollect({}, "", ipConfirmRow);
    setIpConfirmOpen(false);
    setIpConfirmRow(null);
  };

  return (
    <>
      <Box display="flex" justifyContent="end" alignItems="center">
        <Box display="flex" gap={1}>
          <AppointmentCard onClick={() => setSelectedStatus("")} count={statusCount?.total_count || 0} label="All" color="#04B7B1" isSelectedCard={selectedStatus === "" || selectedStatus === null} />
          <AppointmentCard onClick={() => setSelectedStatus("Pending")} count={statusCount?.total_pending_count || 0} label="Pending" color="#616161" isSelectedCard={selectedStatus === "Pending"} />
          <AppointmentCard onClick={() => setSelectedStatus("Waiting For Approval")} count={statusCount?.waiting_for_approval || 0} label="Waiting Approvals" color="#616161" isSelectedCard={selectedStatus === "Waiting For Approval"} />
          <AppointmentCard onClick={() => setSelectedStatus("Approved")} count={statusCount?.total_approved_count || 0} label="Approved" color="#0461B7" isSelectedCard={selectedStatus === "Approved"} />
          <AppointmentCard onClick={() => setSelectedStatus("Cancelled")} count={statusCount?.total_cancelled_count || 0} label="Cancelled" color="#B8170D" isSelectedCard={selectedStatus === "Cancelled"} />
        </Box>
      </Box>

      <Box sx={{ height: "64vh", backgroundColor: "white" }}>
        <DataGrid
          rows={appointments || []}
          columns={columns}
          getRowId={(row) => row.appointment_id}
          pageSizeOptions={pageSizeOptions}
          pagination
          paginationMode="client"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          density="comfortable"
          headerHeight={70}
          rowHeight={45}
          disableRowSelectionOnClick
          sx={{ "& .MuiDataGrid-columnHeaders": { minHeight: 35, maxHeight: 35 } }}
          localeText={{ toolbarColumns: "", toolbarDensity: "", toolbarExport: "", toolbarFilters: "", toolbarExportPrint: "" }}
          components={{
            Toolbar: () => (
              <Box sx={{ p: 0.5 }}>
                <CommonGridToolBarWithFilterText />
              </Box>
            ),
            NoRowsOverlay: () => (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>No schedules found</Box>
            ),
          }}
        />
      </Box>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogContent sx={{ minWidth: 380 }}>
          <Typography>Are you sure you want to cancel this appointment?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} variant="outlined">Close</Button>
          <Button onClick={confirmCancel} variant="contained" color="error">Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={daysConfirmOpen} onClose={() => setDaysConfirmOpen(false)}>
        <DialogTitle>Alert</DialogTitle>
        <DialogContent sx={{ minWidth: 420 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Do You Want to Change the Days ?</Typography>
          <Typography sx={{ mb: 1 }}>
            You are modifying the prescribed duration from <strong>{daysConfirmRow?.original_days ?? "-"}</strong> days to <strong>{daysConfirmRow?.pendingNewDays ?? daysConfirmRow?.days ?? "-"}</strong> days.
          </Typography>
          <Paper elevation={0} sx={{ p: 1, backgroundColor: "#FFECEC", border: "1px solid #E6A4A4" }}>
            <Typography sx={{ color: "#B82121", fontSize: "0.85rem" }}>
              Please note: Changing this will require approval from a doctor. This request has been sent for their review.
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDaysConfirmOpen(false)} variant="outlined">CANCEL</Button>
          <Button onClick={confirmDaysChangeAndRequestApproval} variant="contained" color="error">CONFIRM</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ipConfirmOpen} onClose={() => { setIpConfirmOpen(false); setIpConfirmRow(null); }}>
        <DialogTitle>Alert</DialogTitle>
        <DialogContent sx={{ minWidth: 380 }}>
          <Typography>This patient is converted to In patient. Are you sure you want to do this?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIpConfirmOpen(false); setIpConfirmRow(null); }} variant="outlined">Cancel</Button>
          <Button onClick={confirmIpConversion} variant="contained" color="primary">Yes</Button>
        </DialogActions>
      </Dialog>

      <CollectAdvanceModal
        open={!!(menuRow && openAdvance)}
        onClose={() => { setOpenAdvance(false); setMenuRow(); }}
        onCollect={(data) => advanceCollect(data)}
        patientData={menuRow}
        errorMessage={(msg) => errorMessage(msg)}
        onSkip={() => setOpenSkip(true)}
      />

      {openSkip && (
        <ReasonPopup
          title="Are you sure do you want to skip advance collection process?"
          AlertPopupClose={() => setOpenSkip(false)}
          label="Enter the Skip Reason"
          btntext="Cancel"
          btnvarient="outlined"
          btncolor="error"
          btntext1="Confirm"
          btnvarient1="contained"
          note={true}
          subTitle=""
          sendCmt={(comments) => advanceCollect({}, comments)}
        />
      )}

      {errorMsg && <ToastMsg severity="error" msg={errorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      {isLoading && <Loader />}
    </>
  );
};

export default CounsellorAppointments;
